import React, { useState, useEffect, useMemo } from 'react'
import SimpleNextJSChart from '@/components/charts/SimpleNextJSChart'
import { useNotificationActions } from '@/contexts/NotificationContext'
import { usePOSStore } from '@/store/posStore'
import { Product, StockBatch } from '@/types/pos'
import { AddProductModal } from '@/components/pos/AddProductModal'
import { BarcodeGenerator } from '@/components/pos/BarcodeGenerator'
import { CategoryTreeDropdown } from '@/components/pos/CategoryTreeDropdown'
import { CategoryTreeManage } from '@/components/pos/CategoryTreeManage'
import { buildCategoryTree, type CategoryTreeNode } from '@/lib/categoryTree'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import {
  ScanBarcode,
  Search,
  Edit2,
  Trash2,
  Package,
  AlertTriangle,
  TrendingDown,
  DollarSign,
  BarChart3,
  Printer,
  Clock,
  Layers,
  Plus,
  X,
  Calendar,
  ChevronDown,
  ChevronUp,
  Filter,
  Percent
} from 'lucide-react'

export const InventoryPage: React.FC = () => {
  const {
    products,
    categories,
    suppliers,
    isLoading,
    fetchCategories,
    fetchSuppliers,
    addCategory,
    updateCategory,
    deleteCategory,
    addProduct,
    updateProduct,
    deleteProduct,
    addBatch,
    deleteBatch
  } = usePOSStore()

  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [sortBy, setSortBy] = useState('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  // Advanced filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  const [priceMin, setPriceMin] = useState<string>('')
  const [priceMax, setPriceMax] = useState<string>('')
  const [stockMin, setStockMin] = useState<string>('')
  const [stockMax, setStockMax] = useState<string>('')
  const [supplierId, setSupplierId] = useState<string>('all')
  const [expiryFilter, setExpiryFilter] = useState<'any' | 'has_expiry' | 'expired' | 'expiring_30'>('any')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'low_stock' | 'out_of_stock'>('all')

  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showBarcodeGenerator, setShowBarcodeGenerator] = useState(false)
  const [selectedProductForBarcode, setSelectedProductForBarcode] = useState<Product | null>(null)
  const [currentProduct, setCurrentProduct] = useState<Partial<Product>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [newGenreName, setNewGenreName] = useState('')
  const [newGenreParent, setNewGenreParent] = useState('')
  const [isGenreSubmitting, setIsGenreSubmitting] = useState(false)
  const [showSidebarManage, setShowSidebarManage] = useState(false)

  // Batch management state
  const [showBatchModal, setShowBatchModal] = useState(false)
  const [batchProduct, setBatchProduct] = useState<Product | null>(null)
  const [productBatches, setProductBatches] = useState<StockBatch[]>([])
  const [isBatchLoading, setIsBatchLoading] = useState(false)
  const [showAddBatchForm, setShowAddBatchForm] = useState(false)
  const [newBatch, setNewBatch] = useState({
    sellingPrice: '', costPrice: '', wholesalePrice: '', discountPrice: '', markedPrice: '',
    currentStock: '', receivedDate: new Date().toISOString().split('T')[0], expiryDate: '', notes: ''
  })

  const { showSuccess, showError } = useNotificationActions()

  useEffect(() => {
    fetchCategories()
    fetchSuppliers()
  }, [fetchCategories, fetchSuppliers])

  const slugify = (value: string) => {
    return value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  }

  // Get existing SKUs for uniqueness check
  const existingSKUs = products.map(p => p.sku)

  const handleOpenEditModal = (product: Product) => {
    setCurrentProduct(product)
    setShowEditModal(true)
  }

  const handleAddProduct = async (productData: Partial<Product>) => {
    try {
      await addProduct(productData)
      showSuccess('Book Added', `${productData.name} has been added to inventory.`)
    } catch (error) {
      showError('Failed to Add', 'There was an error adding the book.')
      throw error
    }
  }

  const handleUpdateProduct = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      const productId = currentProduct.id || (currentProduct as any)._id

      // If editing a variant group card, rename all member products instead
      if ((currentProduct as any)._isVariantGroupCard) {
        const groupId = currentProduct.variantGroupId
        const newName = currentProduct.name?.trim()
        if (groupId && newName) {
          const groupMembers = products.filter(p => p.variantGroupId === groupId)
          for (const member of groupMembers) {
            const memberId = member.id || (member as any)._id
            if (memberId && !memberId.startsWith('__vg_')) {
              await updateProduct(memberId, { variantGroupName: newName })
            }
          }
        }
        showSuccess('Group Renamed', `Variant group renamed to "${newName}".`)
        setShowEditModal(false)
        return
      }

      const { id, _id, ...payload }: any = { ...currentProduct }
      if (payload.volumePricing?.rules?.length) {
        const validRules = payload.volumePricing.rules.filter(
          (r: any) => typeof r.blockSize === 'number' && r.blockSize >= 1 && typeof r.pricePerUnit === 'number' && r.pricePerUnit >= 0
        )
        payload.volumePricing = validRules.length ? { rules: validRules } : null
      }
      // Explicitly send null for cleared fields so backend deletes them (undefined gets stripped by JSON.stringify)
      if (!payload.variantGroupId) {
        payload.variantGroupId = null
        payload.variantGroupName = null
      }
      // Strip runtime-only fields
      delete payload._isVariantGroupCard
      delete payload._isVariantMember
      delete payload._masterProductId
      delete payload._salesUnit
      await updateProduct(productId, payload)
      // If variant group name changed, update all other products in the same group
      if (payload.variantGroupId && payload.variantGroupName) {
        const groupMembers = products.filter(
          p => p.variantGroupId === payload.variantGroupId && (p.id || (p as any)._id) !== productId
        )
        for (const member of groupMembers) {
          if (member.variantGroupName !== payload.variantGroupName) {
            const memberId = member.id || (member as any)._id
            await updateProduct(memberId, { variantGroupName: payload.variantGroupName })
          }
        }
      }
      showSuccess('Book Updated', `${currentProduct.name} has been updated.`)
      setShowEditModal(false)
    } catch (error) {
      showError('Update Failed', 'There was an error updating the book.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        await deleteProduct(id)
        showSuccess('Book Deleted', `${name} has been removed.`)
      } catch (error) {
        showError('Delete Failed', 'Could not delete the book.')
      }
    }
  }

  // Batch management handlers
  const handleOpenBatchModal = (product: Product) => {
    const pid = product.id || (product as any)._id
    setBatchProduct(product)
    setShowBatchModal(true)
    // Batches are always live from the Firestore listener — read directly from store
    const state = usePOSStore.getState()
    setProductBatches(state.batches.filter(b => b.productId === pid))
    setIsBatchLoading(false)
  }

  const handleAddBatch = async () => {
    if (!batchProduct) return
    if (!newBatch.currentStock || parseInt(newBatch.currentStock) <= 0) {
      showError('Invalid', 'Stock quantity must be greater than 0')
      return
    }
    if (!newBatch.sellingPrice) {
      showError('Invalid', 'Selling price is required')
      return
    }
    setIsSubmitting(true)
    try {
      await addBatch({
        productId: batchProduct.id || (batchProduct as any)._id,
        sellingPrice: parseFloat(newBatch.sellingPrice) || 0,
        costPrice: parseFloat(newBatch.costPrice) || 0,
        wholesalePrice: newBatch.wholesalePrice ? parseFloat(newBatch.wholesalePrice) : undefined,
        discountPrice: newBatch.discountPrice ? parseFloat(newBatch.discountPrice) : undefined,
        markedPrice: newBatch.markedPrice ? parseFloat(newBatch.markedPrice) : undefined,
        currentStock: parseInt(newBatch.currentStock),
        receivedQuantity: parseInt(newBatch.currentStock),
        receivedDate: newBatch.receivedDate || new Date().toISOString(),
        expiryDate: newBatch.expiryDate || undefined,
        notes: newBatch.notes || undefined,
      } as any)
      showSuccess('Batch Added', 'New stock batch created successfully')
      setShowAddBatchForm(false)
      setNewBatch({
        sellingPrice: '', costPrice: '', wholesalePrice: '', discountPrice: '', markedPrice: '',
        currentStock: '', receivedDate: new Date().toISOString().split('T')[0], expiryDate: '', notes: ''
      })
      // Refresh batches
      handleOpenBatchModal(batchProduct)
    } catch (error) {
      showError('Error', 'Failed to create batch')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteBatch = async (batchId: string) => {
    if (!window.confirm('Delete this batch? Product stock will be adjusted.')) return
    try {
      await deleteBatch(batchId)
      showSuccess('Deleted', 'Batch removed and stock adjusted')
      if (batchProduct) handleOpenBatchModal(batchProduct)
    } catch (error) {
      showError('Error', 'Failed to delete batch')
    }
  }

  const handleCreateGenre = async () => {
    const name = newGenreName.trim()
    if (!name) {
      showError('Invalid Genre', 'Enter a genre name to continue.')
      return
    }
    const parentId = newGenreParent || undefined
    const siblings = categories.filter(c => (c.parentId ?? null) === (parentId || null))
    const nextOrder = siblings.length === 0 ? 0 : Math.max(...siblings.map(c => c.order ?? 0), -1) + 1

    setIsGenreSubmitting(true)
    try {
      await addCategory({
        name,
        slug: slugify(name),
        parentId,
        order: nextOrder,
        isActive: true
      })
      setNewGenreName('')
      setNewGenreParent('')
      showSuccess('Genre Added', `${name} is now available.`)
    } catch (error) {
      showError('Add Failed', 'Could not create the genre.')
    } finally {
      setIsGenreSubmitting(false)
    }
  }

  // Build category hierarchy helpers
  const getChildCategories = (parentId: string) =>
    categories.filter(c => c.parentId === parentId)
  const getCategoryName = (id: string) =>
    categories.find(c => (c.id || (c as any)._id) === id)?.name || ''

  /** All category IDs that are the given category or any of its descendants (for parent filter = show parent + all children) */
  const getCategoryAndDescendantIds = (categoryId: string): string[] => {
    const id = categoryId
    const ids: string[] = [id]
    const children = getChildCategories(id)
    children.forEach(c => {
      const cid = c.id || (c as any)._id
      ids.push(cid, ...getCategoryAndDescendantIds(cid))
    })
    return ids
  }

  const handleDeleteGenre = async (id: string, name: string) => {
    if (window.confirm(`Delete genre "${name}"? This cannot be undone.`)) {
      try {
        await deleteCategory(id)
        if (selectedCategory === id) setSelectedCategory('all')
        showSuccess('Genre Deleted', `${name} has been removed.`)
      } catch (error) {
        showError('Delete Failed', 'Could not delete the genre.')
      }
    }
  }

  const handleMoveGenre = async (id: string, newParentId: string | null) => {
    try {
      await updateCategory(id, { parentId: newParentId || undefined })
      showSuccess('Moved', 'Genre moved successfully.')
    } catch (error) {
      showError('Move Failed', (error as Error).message || 'Could not move genre.')
    }
  }

  const handleReorderGenre = async (id: string, direction: 'up' | 'down') => {
    const cat = categories.find(c => (c.id || (c as any)._id) === id)
    if (!cat) return
    const parentId = cat.parentId ?? null
    const siblings = categories
      .filter(c => (c.parentId ?? null) === parentId)
      .sort((a, b) => {
        const oa = a.order != null ? a.order : 999999
        const ob = b.order != null ? b.order : 999999
        if (oa !== ob) return oa - ob
        return (a.name || '').localeCompare(b.name || '')
      })
    const idx = siblings.findIndex(c => (c.id || (c as any)._id) === id)
    if (idx < 0) return
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1
    if (swapIdx < 0 || swapIdx >= siblings.length) return
    const current = siblings[idx]
    const other = siblings[swapIdx]
    const currentOrder = current.order != null ? current.order : idx
    const otherOrder = other.order != null ? other.order : swapIdx
    try {
      await updateCategory(id, { order: otherOrder })
      await updateCategory(other.id || (other as any)._id, { order: currentOrder })
      showSuccess('Reordered', 'Genre order updated.')
    } catch (error) {
      showError('Reorder Failed', 'Could not reorder.')
    }
  }

  const handleAddSubGenre = (parentId: string | null) => {
    setNewGenreParent(parentId || '')
    setNewGenreName('')
    setShowAdvancedFilters(true)
  }

  const filteredProducts = products
    .filter(product => !(product as any)._salesUnit) // exclude UoM-expanded virtual cards — show master products only
    .filter(product => {
      const nameStr = product.name || ''
      const skuStr = product.sku || ''
      const barcodeStr = product.barcode || ''
      const isbnStr = product.isbn || ''
      const matchesSearch = nameStr.toLowerCase().includes(searchTerm.toLowerCase()) ||
        skuStr.toLowerCase().includes(searchTerm.toLowerCase()) ||
        barcodeStr.toLowerCase().includes(searchTerm.toLowerCase()) ||
        isbnStr.toLowerCase().includes(searchTerm.toLowerCase())
      const catId = product.category && (typeof product.category === 'string' ? product.category : (product.category.id || (product.category as any)._id))
      const categoryIdsForFilter = selectedCategory === 'all' ? null : getCategoryAndDescendantIds(selectedCategory)
      const matchesCategory = selectedCategory === 'all' || (catId != null && categoryIdsForFilter != null && categoryIdsForFilter.includes(catId))

      // Advanced: price range
      const price = product.price ?? 0
      const pMin = priceMin !== '' ? parseFloat(priceMin) : null
      const pMax = priceMax !== '' ? parseFloat(priceMax) : null
      const matchesPrice = (pMin == null || price >= pMin) && (pMax == null || price <= pMax)

      // Advanced: stock range
      const stock = product.stock ?? 0
      const sMin = stockMin !== '' ? parseInt(stockMin, 10) : null
      const sMax = stockMax !== '' ? parseInt(stockMax, 10) : null
      const matchesStock = (sMin == null || stock >= sMin) && (sMax == null || stock <= sMax)

      // Advanced: supplier
      const prodSupplierId = product.supplierId ?? (typeof product.supplier === 'object' ? (product.supplier?.id ?? (product.supplier as any)?._id) : undefined)
      const matchesSupplier = supplierId === 'all' || prodSupplierId === supplierId

      // Advanced: expiry
      const expDate = product.expiryDate ? new Date(product.expiryDate) : null
      const now = new Date()
      const daysUntil = expDate ? Math.ceil((expDate.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)) : null
      const matchesExpiry =
        expiryFilter === 'any' ? true
          : expiryFilter === 'has_expiry' ? expDate != null
            : expiryFilter === 'expired' ? daysUntil != null && daysUntil <= 0
              : daysUntil != null && daysUntil > 0 && daysUntil <= 30

      // Advanced: status (active = in stock and not low; low_stock; out_of_stock)
      const reorderThresh = product.reorderLevel ?? product.minStock ?? 0
      const matchesStatus =
        statusFilter === 'all' ? true
          : statusFilter === 'out_of_stock' ? stock === 0
            : statusFilter === 'low_stock' ? stock > 0 && stock <= reorderThresh
              : stock > reorderThresh

      return matchesSearch && matchesCategory && matchesPrice && matchesStock && matchesSupplier && matchesExpiry && matchesStatus
    })
    .sort((a, b) => {
      let aValue: any = a[sortBy as keyof Product]
      let bValue: any = b[sortBy as keyof Product]

      if (sortBy === 'category') {
        aValue = typeof a.category === 'string' ? a.category : a.category?.name || ''
        bValue = typeof b.category === 'string' ? b.category : b.category?.name || ''
      }

      if (typeof aValue === 'string') aValue = aValue.toLowerCase()
      if (typeof bValue === 'string') bValue = bValue.toLowerCase()

      if (sortOrder === 'asc') return aValue < bValue ? -1 : 1
      return aValue > bValue ? -1 : 1
    })

  // Only count master/non-UoM products for stats — exclude virtual UoM sales-unit cards
  // (virtual cards have _masterProductId set and would double-count stock/value)
  const masterProducts = products.filter(p => !(p as any)._masterProductId)

  const lowStockProducts = masterProducts.filter(product => product.stock <= product.minStock && product.stock > 0)
  const outOfStockProducts = masterProducts.filter(product => product.stock === 0)

  // Cost value and Retail value — UoM products need special handling:
  // stock is in base units; product.cost and product.price may be per-receiving-unit (e.g. per bundle).
  // For UoM, convert to default sales unit qty and use that unit's prices.
  // e.g. 49,500 sheets ÷ 500 = 99 bundles; cost = 99 × Rs.950; retail = 99 × Rs.1,200
  const getUoMStats = (product: Product) => {
    const salesUnits = (product as any).salesUnits as any[] | undefined
    if (salesUnits && salesUnits.length > 0) {
      const defaultSU = salesUnits.find((su: any) => su.isDefault) || salesUnits[0]
      const factor = defaultSU.conversionFactor || 1
      const suQty = Math.floor(product.stock / factor)
      return { suQty, factor, defaultSU }
    }
    return null
  }

  const totalInventoryValue = masterProducts.reduce((sum, product) => {
    const uom = getUoMStats(product)
    if (uom) {
      // product.cost is per receiving/sales unit (e.g. cost per bundle)
      return sum + (uom.suQty * (product.cost || 0))
    }
    return sum + (product.stock * (product.cost || 0))
  }, 0)

  const totalRetailValue = masterProducts.reduce((sum, product) => {
    const uom = getUoMStats(product)
    if (uom) {
      return sum + (uom.suQty * (uom.defaultSU.price || 0))
    }
    return sum + (product.stock * (product.price || (product as any).salePrice || 0))
  }, 0)

  const stockLevelData = filteredProducts.slice(0, 8).map(p => ({
    date: p.name,
    value: p.stock,
    label: `${p.name} Stock`
  }))

  // Variant groups derived from products
  const variantGroups = useMemo(() => {
    const groups = new Map<string, string>()
    for (const p of products) {
      if (p.variantGroupId && p.variantGroupName) {
        groups.set(p.variantGroupId, p.variantGroupName)
      }
    }
    return Array.from(groups.entries()).map(([id, name]) => ({ id, name }))
  }, [products])

  // Expiry tracking
  const expiryData = useMemo(() => {
    const now = new Date()
    const expiredItems: (Product & { daysUntilExpiry: number })[] = []
    const expiringSoon: (Product & { daysUntilExpiry: number })[] = [] // within 30 days

    products.forEach(p => {
      if (!p.expiryDate) return
      const expDate = new Date(p.expiryDate)
      const diffDays = Math.ceil((expDate.getTime() - now.getTime()) / (24 * 60 * 60 * 1000))
      const item = { ...p, daysUntilExpiry: diffDays }
      if (diffDays <= 0) {
        expiredItems.push(item)
      } else if (diffDays <= 30) {
        expiringSoon.push(item)
      }
    })

    return { expiredItems, expiringSoon }
  }, [products])

  return (
    <div className="min-h-screen bg-gradient-space p-3 sm:p-4 lg:p-6">
      <div className="w-full">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold esales-text font-display">Inventory Management</h1>
            <p className="text-muted-foreground mt-2">Manage your book collection across all genres.</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowAddModal(true)}
              className="glow-button flex items-center gap-2"
            >
              <ScanBarcode className="w-5 h-5" />
              Add via Barcode
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6 lg:gap-8 mb-4">
          {[
            {
              label: 'Total Books',
              value: masterProducts.length,
              icon: <Package className="w-6 h-6" />,
              color: 'text-primary',
              bgColor: 'bg-primary/10'
            },
            {
              label: 'Low Stock',
              value: lowStockProducts.length,
              icon: <AlertTriangle className="w-6 h-6" />,
              color: 'text-yellow-400',
              bgColor: 'bg-yellow-400/10'
            },
            {
              label: 'Out of Stock',
              value: outOfStockProducts.length,
              icon: <TrendingDown className="w-6 h-6" />,
              color: 'text-red-400',
              bgColor: 'bg-red-400/10'
            },
            {
              label: 'Cost Value',
              value: `Rs.${totalInventoryValue.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              icon: <DollarSign className="w-6 h-6" />,
              color: 'text-blue-400',
              bgColor: 'bg-blue-400/10'
            },
            {
              label: 'Retail Value',
              value: `Rs.${totalRetailValue.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              icon: <BarChart3 className="w-6 h-6" />,
              color: 'text-green-400',
              bgColor: 'bg-green-400/10'
            }
          ].map((stat, i) => (
            <div key={i} className="glass-card hover-lift flex items-center justify-between py-4 px-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wider">{stat.label}</p>
                <p className={`text-xl font-bold ${stat.color}`}>{stat.value}</p>
              </div>
              <div className={`p-3 rounded-xl ${stat.bgColor} ${stat.color}`}>
                {stat.icon}
              </div>
            </div>
          ))}
        </div>

        {/* Expiry Alerts Banner */}
        {(expiryData.expiredItems.length > 0 || expiryData.expiringSoon.length > 0) && (
          <div className="mb-4 space-y-3">
            {expiryData.expiredItems.length > 0 && (
              <div className="glass-card !border-red-500/30 !bg-red-500/5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-red-500/10">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-red-400">Expired Items ({expiryData.expiredItems.length})</h3>
                    <p className="text-xs text-muted-foreground">These items have passed their expiry date and should be removed from sale.</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {expiryData.expiredItems.slice(0, 6).map(item => (
                    <div key={item.id || (item as any)._id} className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 border border-red-500/20 rounded-lg">
                      <span className="text-sm font-medium text-red-400">{item.name}</span>
                      <span className="text-[10px] text-red-300 font-mono">{Math.abs(item.daysUntilExpiry)}d ago</span>
                    </div>
                  ))}
                  {expiryData.expiredItems.length > 6 && (
                    <span className="text-xs text-red-300 px-2 py-1.5">+{expiryData.expiredItems.length - 6} more</span>
                  )}
                </div>
              </div>
            )}
            {expiryData.expiringSoon.length > 0 && (
              <div className="glass-card !border-yellow-500/30 !bg-yellow-500/5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-yellow-500/10">
                    <Clock className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-yellow-400">Expiring Soon ({expiryData.expiringSoon.length})</h3>
                    <p className="text-xs text-muted-foreground">These items will expire within the next 30 days.</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {expiryData.expiringSoon.map(item => (
                    <div key={item.id || (item as any)._id} className="flex items-center gap-2 px-3 py-1.5 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                      <span className="text-sm font-medium text-yellow-400">{item.name}</span>
                      <span className="text-[10px] text-yellow-300 font-mono">{item.daysUntilExpiry}d left</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Main layout: Category Sidebar + Content */}
        <div className="flex gap-4 mb-4">

          {/* Category Sidebar — browse only */}
          <div className="w-48 shrink-0">
            <div className="glass-card p-0 overflow-hidden sticky top-4">
              {/* Header */}
              <div className="px-3 py-2 border-b border-border/50 bg-primary/5 flex items-center justify-between">
                <p className="text-[10px] font-black uppercase tracking-widest text-primary">Genres</p>
                <button
                  type="button"
                  onClick={() => setShowSidebarManage(prev => !prev)}
                  className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded transition-colors ${showSidebarManage ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-primary'}`}
                  title="Manage genres"
                >
                  {showSidebarManage ? 'Close' : 'Manage'}
                </button>
              </div>

              {/* Category filter list */}
              <div className="overflow-y-auto max-h-[65vh]">
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={`w-full text-left px-3 py-2 text-sm font-semibold transition-colors border-l-2 ${selectedCategory === 'all' ? 'border-primary bg-primary/10 text-primary' : 'border-transparent hover:bg-muted/50 text-foreground'}`}
                >
                  All Genres
                </button>
                {buildCategoryTree(categories).map((node: CategoryTreeNode) => {
                  const nodeId = node.category.id || (node.category as any)._id
                  return (
                    <React.Fragment key={nodeId}>
                      <button
                        onClick={() => setSelectedCategory(nodeId)}
                        className={`w-full text-left px-3 py-2 text-sm font-medium transition-colors border-l-2 ${selectedCategory === nodeId ? 'border-primary bg-primary/10 text-primary' : 'border-transparent hover:bg-muted/50 text-foreground'}`}
                      >
                        {node.category.icon && <span className="mr-1 text-xs">{node.category.icon}</span>}
                        {node.category.name}
                      </button>
                      {node.children?.map((child: CategoryTreeNode) => {
                        const childId = child.category.id || (child.category as any)._id
                        return (
                          <button
                            key={childId}
                            onClick={() => setSelectedCategory(childId)}
                            className={`w-full text-left pl-6 pr-3 py-1.5 text-xs font-medium transition-colors border-l-2 ${selectedCategory === childId ? 'border-primary bg-primary/10 text-primary' : 'border-transparent hover:bg-muted/50 text-muted-foreground'}`}
                          >
                            {child.category.icon && <span className="mr-1">{child.category.icon}</span>}
                            {child.category.name}
                          </button>
                        )
                      })}
                    </React.Fragment>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">

        {/* Advanced Filters panel (collapsible, sits above product table) */}
        {showAdvancedFilters && (
          <div className="glass-card mb-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Price range (Rs.)</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    placeholder="Min"
                    value={priceMin}
                    onChange={e => setPriceMin(e.target.value)}
                    className="w-full px-3 py-2 bg-background/50 border border-border rounded-lg text-sm"
                  />
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    placeholder="Max"
                    value={priceMax}
                    onChange={e => setPriceMax(e.target.value)}
                    className="w-full px-3 py-2 bg-background/50 border border-border rounded-lg text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Stock range</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min={0}
                    placeholder="Min"
                    value={stockMin}
                    onChange={e => setStockMin(e.target.value)}
                    className="w-full px-3 py-2 bg-background/50 border border-border rounded-lg text-sm"
                  />
                  <input
                    type="number"
                    min={0}
                    placeholder="Max"
                    value={stockMax}
                    onChange={e => setStockMax(e.target.value)}
                    className="w-full px-3 py-2 bg-background/50 border border-border rounded-lg text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Status</label>
                <select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value as typeof statusFilter)}
                  className="w-full px-3 py-2 bg-background/50 border border-border rounded-lg text-sm"
                >
                  <option value="all">All</option>
                  <option value="active">Active (above reorder)</option>
                  <option value="low_stock">Low stock</option>
                  <option value="out_of_stock">Out of stock</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Supplier</label>
                <select
                  value={supplierId}
                  onChange={e => setSupplierId(e.target.value)}
                  className="w-full px-3 py-2 bg-background/50 border border-border rounded-lg text-sm"
                >
                  <option value="all">All suppliers</option>
                  {suppliers.map(s => (
                    <option key={s.id || (s as any)._id} value={s.id || (s as any)._id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Expiry</label>
                <select
                  value={expiryFilter}
                  onChange={e => setExpiryFilter(e.target.value as typeof expiryFilter)}
                  className="w-full px-3 py-2 bg-background/50 border border-border rounded-lg text-sm"
                >
                  <option value="any">Any</option>
                  <option value="has_expiry">Has expiry date</option>
                  <option value="expired">Expired</option>
                  <option value="expiring_30">Expiring within 30 days</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => {
                    setPriceMin('')
                    setPriceMax('')
                    setStockMin('')
                    setStockMax('')
                    setSupplierId('all')
                    setExpiryFilter('any')
                    setStatusFilter('all')
                  }}
                  className="px-3 py-2 text-sm rounded-lg bg-muted hover:bg-muted/80"
                >
                  Clear filters
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="glass-card overflow-hidden flex flex-col">
          {/* Search + Sort bar above the product list */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4 pb-4 border-b border-border/50">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by title, SKU, ISBN, or barcode..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-background/50 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:outline-none"
              />
            </div>
            <div className="flex gap-2 shrink-0">
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="px-3 py-2 text-sm bg-background/50 border border-border rounded-lg"
              >
                <option value="name">Title</option>
                <option value="stock">Stock</option>
                <option value="price">Price</option>
                <option value="category">Genre</option>
              </select>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="px-3 py-2 bg-muted rounded-lg hover:bg-muted/80 transition-colors text-sm"
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
              <button
                type="button"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors ${showAdvancedFilters ? 'bg-primary/20 text-primary' : 'bg-muted hover:bg-muted/80'}`}
              >
                <Filter className="w-3.5 h-3.5" />
                Advanced
                {showAdvancedFilters ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>

          {isLoading ? (
            <LoadingSpinner
              size="md"
              message="Loading Inventory"
              submessage="Syncing collection data..."
            />
          ) : filteredProducts.length === 0 ? (
            <div className="py-20 text-center">
              <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-lg font-semibold mb-2">No products found</p>
              <p className="text-muted-foreground text-sm mb-4">
                {searchTerm ? 'Try a different search term' : 'Start by adding your first product'}
              </p>
              <button
                onClick={() => setShowAddModal(true)}
                className="glow-button"
              >
                <ScanBarcode className="w-4 h-4 mr-2 inline" />
                Add Product
              </button>
            </div>
          ) : (
            <div className="overflow-auto max-h-[60vh]">
            <table className="w-full text-left border-collapse">
              <thead className="sticky top-0 z-10 bg-card">
                <tr className="border-b border-border text-muted-foreground uppercase text-xs">
                  <th className="py-4 px-4">Product Details</th>
                  <th className="py-4 px-4">SKU / Barcode</th>
                  <th className="py-4 px-4">Genre</th>
                  <th className="py-4 px-4">Stock</th>
                  <th className="py-4 px-4">Price</th>
                  <th className="py-4 px-4">Expiry</th>
                  <th className="py-4 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map(product => (
                  <tr key={product.id || (product as any)._id} className="border-b border-border/50 hover:bg-muted/10 transition-colors">
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-3">
                        {(product.image?.startsWith('http') || product.image?.startsWith('data:')) ? (
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-10 h-14 object-cover rounded shadow"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none'
                                ; (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden')
                            }}
                          />
                        ) : null}
                        <span className={`text-2xl ${(product.image?.startsWith('http') || product.image?.startsWith('data:')) ? 'hidden' : ''}`}>
                          {product.image && !product.image.startsWith('http') && !product.image.startsWith('data:') ? product.image : '📖'}
                        </span>
                        <div>
                          <p className="font-bold text-foreground flex items-center gap-1.5">
                            {product.name}
                            {product.variantGroupId && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-400 text-[10px] font-medium">
                                <Layers className="w-3 h-3" />
                                {product.variantGroupName}
                              </span>
                            )}
                          </p>
                          {(product as any).salesUnits?.length > 0 ? (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {(product as any).salesUnits.map((su: any) => (
                                <span key={su.id} className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">
                                  {product.name} - {su.name}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground line-clamp-1 max-w-[200px]">{product.description || 'No description'}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div>
                        <p className="font-mono text-sm font-semibold">{product.sku}</p>
                        {(product.barcode || product.isbn) && (
                          <p className="font-mono text-[10px] text-muted-foreground">{product.barcode || product.isbn}</p>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      {(() => {
                        const cat = typeof product.category === 'string'
                          ? categories.find(c => (c.id || (c as any)._id) === product.category)
                          : product.category
                        const catName = cat?.name || (typeof product.category === 'string' ? product.category : 'General')
                        const parentName = cat?.parentId ? getCategoryName(cat.parentId) : ''
                        return (
                          <div className="flex flex-col gap-0.5">
                            {parentName && (
                              <span className="text-[9px] text-muted-foreground">{parentName}</span>
                            )}
                            <span className="px-2 py-0.5 bg-primary/20 text-primary rounded-full text-[10px] font-bold uppercase inline-block w-fit">
                              {parentName ? `└ ${catName}` : catName}
                            </span>
                          </div>
                        )
                      })()}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center space-x-2">
                          <span className={`font-bold ${product.stock === 0 ? 'text-red-400' :
                            product.stock <= (product.minStock ?? 0) ? 'text-yellow-400' :
                              'text-green-400'
                            }`}>
                            {product.stock.toLocaleString()}
                          </span>
                          {(product as any).baseUnit && (
                            <span className="text-[10px] text-primary font-semibold">{(product as any).baseUnit}</span>
                          )}
                          <span className="text-[10px] text-muted-foreground">/ {product.minStock ?? 0} min</span>
                        </div>
                        {(product as any).salesUnits?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {(product as any).salesUnits.map((su: any) => (
                              <span key={su.id} className="text-[9px] px-1 py-0.5 bg-muted/50 rounded text-muted-foreground">
                                {su.name}: {Math.floor(product.stock / su.conversionFactor).toLocaleString()}
                              </span>
                            ))}
                          </div>
                        )}
                        {product.stock === 0 && (
                          <span className="text-[10px] text-red-400 font-semibold">OUT OF STOCK</span>
                        )}
                        {product.stock > 0 && (product.reorderLevel ?? product.minStock ?? 0) > 0 && product.stock <= (product.reorderLevel ?? product.minStock ?? 0) && (
                          <span className="text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-600 dark:text-amber-400 w-fit">Below reorder</span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      {(product as any).salesUnits?.length > 0 ? (
                        <div className="space-y-0.5">
                          {(product as any).salesUnits.map((su: any) => (
                            <p key={su.id} className="text-xs text-muted-foreground">
                              <span className="font-semibold text-primary">{su.name}:</span> Rs.{Number(su.price || 0).toFixed(2)}
                              {su.wholesalePrice ? <span className="text-blue-400 ml-1">(W/S: Rs.{Number(su.wholesalePrice).toFixed(2)})</span> : null}
                            </p>
                          ))}
                        </div>
                      ) : (
                        <>
                          <p className="font-bold text-primary">Rs.{(product.price ?? 0).toFixed(2)}</p>
                          {product.discountPrice ? (
                            <p className="text-[10px] text-yellow-400 font-semibold">Promo: Rs.{product.discountPrice.toFixed(2)}</p>
                          ) : null}
                          {product.wholesalePrice ? (
                            <p className="text-[10px] text-blue-400">W/S: Rs.{product.wholesalePrice.toFixed(2)}</p>
                          ) : null}
                          {product.markedPrice ? (
                            <p className="text-[10px] text-muted-foreground line-through">Marked: Rs.{product.markedPrice.toFixed(2)}</p>
                          ) : null}
                        </>
                      )}
                      {product.cost ? (
                        <p className="text-[10px] text-muted-foreground">Cost: Rs.{product.cost.toFixed(2)}</p>
                      ) : null}
                    </td>
                    <td className="py-4 px-4">
                      {product.expiryDate ? (() => {
                        const now = new Date()
                        const exp = new Date(product.expiryDate)
                        const diffDays = Math.ceil((exp.getTime() - now.getTime()) / (24 * 60 * 60 * 1000))
                        const isExpired = diffDays <= 0
                        const isExpiringSoon = diffDays > 0 && diffDays <= 30
                        return (
                          <div>
                            <p className={`text-xs font-mono ${isExpired ? 'text-red-400 font-bold' : isExpiringSoon ? 'text-yellow-400' : 'text-muted-foreground'}`}>
                              {exp.toLocaleDateString()}
                            </p>
                            {isExpired && <span className="text-[10px] text-red-400 font-bold">EXPIRED</span>}
                            {isExpiringSoon && <span className="text-[10px] text-yellow-400">{diffDays}d left</span>}
                          </div>
                        )
                      })() : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex space-x-2">
                        {(product.barcode || product.isbn) && (
                          <button
                            onClick={() => {
                              setSelectedProductForBarcode(product)
                              setShowBarcodeGenerator(true)
                            }}
                            className="p-2 rounded-lg hover:bg-blue-500/10 text-blue-400 transition-colors"
                            title="Print Barcode"
                          >
                            <Printer className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleOpenBatchModal(product)}
                          className="p-2 rounded-lg hover:bg-amber-500/10 text-amber-400 transition-colors"
                          title="Stock Batches"
                        >
                          <Layers className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleOpenEditModal(product)}
                          className="p-2 rounded-lg hover:bg-primary/10 text-primary transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(product.id || (product as any)._id, product.name)}
                          className="p-2 rounded-lg hover:bg-red-500/10 text-red-400 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>

        {/* Charts */}
        {stockLevelData.length > 0 && (
          <div className="glass-card mb-8">
            <h3 className="text-xl font-bold esales-text mb-4">Stock Levels (Top 8 Products)</h3>
            <div className="h-84">
              <SimpleNextJSChart
                data={stockLevelData.map(d => ({ x: d.date, y: d.value, label: d.label }))}
                type="bar"
                color="var(--primary)"
              />
            </div>
          </div>
        )}

          </div>{/* end Main Content */}
        </div>{/* end flex layout */}

        {/* Genre Management Panel — full width, shown when Manage is toggled */}
        {showSidebarManage && (
          <div className="glass-card mb-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-bold text-foreground">Genre Management</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Add, edit, reorder, and organise your genre hierarchy</p>
              </div>
              <button
                type="button"
                onClick={() => setShowSidebarManage(false)}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-muted hover:bg-muted/80 text-foreground transition-colors"
              >
                Close
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: Add new genre */}
              <div>
                <p className="text-xs font-black uppercase tracking-wider text-muted-foreground mb-3">Add New Genre</p>
                <div className="space-y-2">
                  <input
                    type="text"
                    placeholder="Genre name..."
                    value={newGenreName}
                    onChange={(e) => setNewGenreName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleCreateGenre() }}
                    className="w-full px-3 py-2 text-sm bg-background/50 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                  />
                  <select
                    value={newGenreParent}
                    onChange={(e) => setNewGenreParent(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-background/50 border border-border rounded-lg"
                  >
                    <option value="">Root (no parent)</option>
                    {(() => {
                      const tree = buildCategoryTree(categories)
                      const options: { id: string; name: string; depth: number }[] = []
                      function collect(nodes: CategoryTreeNode[], depth: number) {
                        nodes.forEach(node => {
                          const id = node.category.id || (node.category as any)._id
                          options.push({ id, name: node.category.name, depth })
                          if (node.children.length) collect(node.children, depth + 1)
                        })
                      }
                      collect(tree, 0)
                      return options.map(opt => (
                        <option key={opt.id} value={opt.id}>
                          {'\u00A0'.repeat(opt.depth * 2)}└ {opt.name}
                        </option>
                      ))
                    })()}
                  </select>
                  <button
                    onClick={handleCreateGenre}
                    disabled={isGenreSubmitting}
                    className="w-full py-2 text-sm rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-60 font-semibold"
                  >
                    {isGenreSubmitting ? 'Adding...' : '+ Add Genre'}
                  </button>
                </div>
              </div>

              {/* Right: Hierarchy tree */}
              <div>
                <p className="text-xs font-black uppercase tracking-wider text-muted-foreground mb-3">Genre Hierarchy</p>
                <div className="border border-border/50 rounded-xl p-2 bg-background/30 max-h-[50vh] overflow-y-auto">
                  <CategoryTreeManage
                    categories={categories}
                    onEdit={async (categoryId, updates) => {
                      try {
                        await updateCategory(categoryId, updates)
                        showSuccess('Genre Updated', 'Name saved.')
                      } catch (e) {
                        showError('Update Failed', (e as Error).message || 'Could not update.')
                      }
                    }}
                    onDelete={(categoryId) => {
                      const c = categories.find(x => (x.id || (x as any)._id) === categoryId)
                      if (c) handleDeleteGenre(categoryId, c.name)
                    }}
                    onAddSub={handleAddSubGenre}
                    onMove={handleMoveGenre}
                    onReorder={handleReorderGenre}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Product Modal (with Barcode Scanning) */}
        {showAddModal && (
          <AddProductModal
            categories={categories}
            existingSKUs={existingSKUs}
            onSave={handleAddProduct}
            onClose={() => setShowAddModal(false)}
          />
        )}

        {/* Barcode Generator Modal */}
        {showBarcodeGenerator && selectedProductForBarcode && (
          <BarcodeGenerator
            barcode={selectedProductForBarcode.barcode || selectedProductForBarcode.isbn || ''}
            productName={selectedProductForBarcode.name}
            sku={selectedProductForBarcode.sku}
            onClose={() => {
              setShowBarcodeGenerator(false)
              setSelectedProductForBarcode(null)
            }}
            onPrint={() => {
              showSuccess('Barcode Printed', 'Barcode label has been sent to printer.')
            }}
          />
        )}

        {/* Edit Modal */}
        {showEditModal && (currentProduct as any)._isVariantGroupCard && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="glass-card max-w-sm w-full">
              <h2 className="text-2xl font-bold mb-4 esales-text flex items-center gap-2">
                <Layers className="w-5 h-5" />
                Rename Variant Group
              </h2>
              <form onSubmit={handleUpdateProduct} className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-muted-foreground uppercase">Group Name</label>
                  <input
                    required
                    autoFocus
                    value={currentProduct.name || ''}
                    onChange={e => setCurrentProduct({ ...currentProduct, name: e.target.value })}
                    className="w-full mt-1 px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                    placeholder="e.g. A4 Paper"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    This will rename the group across all {(currentProduct as any).description?.match(/\d+/)?.[0] || ''} member products.
                  </p>
                </div>
                <div className="flex gap-4">
                  <button type="button" onClick={() => setShowEditModal(false)} className="flex-1 py-3 bg-muted rounded-lg font-bold hover:bg-muted/80 transition-colors">Cancel</button>
                  <button type="submit" disabled={isSubmitting} className="flex-1 py-3 bg-primary text-white rounded-lg font-bold glow-effect disabled:opacity-50">
                    {isSubmitting ? 'Saving...' : 'Rename Group'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Product Modal */}
        {showEditModal && !(currentProduct as any)._isVariantGroupCard && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="glass-card max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-6 esales-text">Edit Book</h2>
              <form onSubmit={handleUpdateProduct} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="text-xs font-bold text-muted-foreground uppercase">Book Title</label>
                    <input
                      required
                      value={currentProduct.name}
                      onChange={e => setCurrentProduct({ ...currentProduct, name: e.target.value })}
                      className="w-full mt-1 px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs font-bold text-muted-foreground uppercase">Description</label>
                    <input
                      value={currentProduct.description}
                      onChange={e => setCurrentProduct({ ...currentProduct, description: e.target.value })}
                      className="w-full mt-1 px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-muted-foreground uppercase">SKU</label>
                    <input
                      required
                      value={currentProduct.sku}
                      onChange={e => setCurrentProduct({ ...currentProduct, sku: e.target.value })}
                      className="w-full mt-1 px-4 py-2 bg-background border border-border rounded-lg font-mono"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-muted-foreground uppercase">Barcode/ISBN</label>
                    <input
                      value={currentProduct.barcode || currentProduct.isbn || ''}
                      onChange={e => setCurrentProduct({ ...currentProduct, barcode: e.target.value, isbn: e.target.value })}
                      className="w-full mt-1 px-4 py-2 bg-background border border-border rounded-lg font-mono"
                    />
                  </div>

                  {/* Variant Group */}
                  <div className="col-span-2">
                    <div className="flex items-center gap-2 mb-1">
                      <Layers className="w-4 h-4 text-violet-400" />
                      <label className="text-xs font-bold text-muted-foreground uppercase">Variant Group</label>
                    </div>
                    <p className="text-[9px] text-muted-foreground mb-2">Link products that are the same item in different colors/sizes (e.g. A4 Paper → White, Purple, Blue)</p>
                    <select
                      value={currentProduct.variantGroupId || '_none'}
                      onChange={e => {
                        const val = e.target.value
                        if (val === '_none') {
                          setCurrentProduct({ ...currentProduct, variantGroupId: undefined, variantGroupName: undefined })
                        } else if (val === '_new') {
                          const name = prompt('Enter variant group name (e.g. "A4 Paper"):')
                          if (name && name.trim()) {
                            setCurrentProduct({
                              ...currentProduct,
                              variantGroupId: `vg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                              variantGroupName: name.trim(),
                            })
                          }
                        } else {
                          const group = variantGroups.find(g => g.id === val)
                          if (group) {
                            setCurrentProduct({ ...currentProduct, variantGroupId: group.id, variantGroupName: group.name })
                          }
                        }
                      }}
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                    >
                      <option value="_none">None (standalone product)</option>
                      <option value="_new">+ Create New Group...</option>
                      {variantGroups.map(g => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                    {currentProduct.variantGroupId && (
                      <div className="mt-2 space-y-1.5">
                        <div className="flex items-center gap-2">
                          <label className="text-[10px] font-bold text-violet-400 whitespace-nowrap">Group Name:</label>
                          <input
                            value={currentProduct.variantGroupName || ''}
                            onChange={e => setCurrentProduct({ ...currentProduct, variantGroupName: e.target.value })}
                            className="flex-1 px-2 py-1 bg-background border border-violet-500/30 rounded text-sm text-violet-300 focus:ring-1 focus:ring-violet-500 focus:outline-none"
                            placeholder="Group name..."
                          />
                        </div>
                        <p className="text-[10px] text-muted-foreground">
                          {products.filter(p => p.variantGroupId === currentProduct.variantGroupId).length} product(s) in this group — renaming updates all members on save
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Book Cover Image */}
                  <div className="col-span-2">
                    <label className="text-xs font-bold text-muted-foreground uppercase mb-1 block">Book Cover</label>
                    <div className="flex gap-3">
                      {/* Image Preview */}
                      <div className="w-16 h-20 bg-muted/20 border border-border rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                        {(currentProduct.image?.startsWith('http') || currentProduct.image?.startsWith('data:')) ? (
                          <img
                            src={currentProduct.image}
                            alt="Book cover"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none'
                            }}
                          />
                        ) : (
                          <span className="text-2xl text-muted-foreground">{currentProduct.image || '📖'}</span>
                        )}
                      </div>

                      {/* Upload Options */}
                      <div className="flex-1 space-y-2">
                        <input
                          type="url"
                          value={(currentProduct.image?.startsWith('http')) ? currentProduct.image : ''}
                          onChange={(e) => setCurrentProduct({ ...currentProduct, image: e.target.value })}
                          placeholder="Paste image URL..."
                          className="w-full px-4 py-2 bg-background border border-border rounded-lg text-sm"
                        />
                        <div className="flex gap-2">
                          <label className="flex-1 px-3 py-2 bg-muted/50 hover:bg-muted border border-border rounded-lg cursor-pointer flex items-center justify-center gap-2 transition-colors text-sm">
                            <span>📤 Upload</span>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0]
                                if (file) {
                                  if (file.size > 2 * 1024 * 1024) {
                                    alert('Image size must be less than 2MB')
                                    return
                                  }
                                  const reader = new FileReader()
                                  reader.onloadend = () => {
                                    setCurrentProduct({ ...currentProduct, image: reader.result as string })
                                  }
                                  reader.readAsDataURL(file)
                                }
                              }}
                            />
                          </label>
                          {currentProduct.image && (
                            <button
                              type="button"
                              onClick={() => setCurrentProduct({ ...currentProduct, image: '' })}
                              className="px-3 py-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors text-sm"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-muted-foreground uppercase">Genre</label>
                    <div className="mt-1">
                      <CategoryTreeDropdown
                        categories={categories}
                        value={
                          currentProduct.category == null
                            ? 'all'
                            : typeof currentProduct.category === 'string'
                              ? currentProduct.category
                              : (currentProduct.category?.id ?? (currentProduct.category as any)?._id ?? 'all')
                        }
                        onSelect={(id) => {
                          const cat = id === 'all' || !id ? null : categories.find(c => (c.id || (c as any)._id) === id)
                          setCurrentProduct({ ...currentProduct, category: (cat ?? null) as any })
                        }}
                        allLabel="No category / Uncategorized"
                        placeholder="Genre"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-muted-foreground uppercase">Retail Price (Rs.)</label>
                    <input
                      type="number" step="0.01" required
                      value={currentProduct.price}
                      onChange={e => setCurrentProduct({ ...currentProduct, price: parseFloat(e.target.value) })}
                      className="w-full mt-1 px-4 py-2 bg-background border border-border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-muted-foreground uppercase">Cost Price (Rs.)</label>
                    <p className="text-[9px] text-muted-foreground pl-0.5">Purchase price from supplier</p>
                    <input
                      type="number" step="0.01" required
                      value={currentProduct.cost}
                      onChange={e => setCurrentProduct({ ...currentProduct, cost: parseFloat(e.target.value) })}
                      className="w-full mt-1 px-4 py-2 bg-background border border-border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-muted-foreground uppercase">Wholesale Price (Rs.)</label>
                    <p className="text-[9px] text-muted-foreground pl-0.5">Price for bulk selling</p>
                    <input
                      type="number" step="0.01"
                      value={currentProduct.wholesalePrice || ''}
                      onChange={e => setCurrentProduct({ ...currentProduct, wholesalePrice: parseFloat(e.target.value) || 0 })}
                      placeholder="Optional"
                      className="w-full mt-1 px-4 py-2 bg-background border border-border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-muted-foreground uppercase">Discount Price (Rs.)</label>
                    <p className="text-[9px] text-muted-foreground pl-0.5">Promotional price (auto-applies at POS)</p>
                    <input
                      type="number" step="0.01"
                      value={currentProduct.discountPrice || ''}
                      onChange={e => setCurrentProduct({ ...currentProduct, discountPrice: parseFloat(e.target.value) || 0 })}
                      placeholder="Optional - set for active promotions"
                      className="w-full mt-1 px-4 py-2 bg-background border border-border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-muted-foreground uppercase">Marked Price (Rs.)</label>
                    <p className="text-[9px] text-muted-foreground pl-0.5">Manufacturer's issued price</p>
                    <input
                      type="number" step="0.01"
                      value={currentProduct.markedPrice || ''}
                      onChange={e => setCurrentProduct({ ...currentProduct, markedPrice: parseFloat(e.target.value) || 0 })}
                      placeholder="Optional"
                      className="w-full mt-1 px-4 py-2 bg-background border border-border rounded-lg"
                    />
                  </div>
                  {/* Volume pricing (customer-facing) */}
                  <div className="col-span-2">
                    <div className="flex items-center gap-2 mb-2">
                      <Percent className="w-4 h-4 text-primary" />
                      <label className="text-xs font-bold text-muted-foreground uppercase">Volume Pricing (Customer)</label>
                    </div>
                    <p className="text-[9px] text-muted-foreground pl-0.5 mb-2">e.g. Every 500 units @ Rs.2.50 each; remainder at retail price.</p>
                    <div className="space-y-2 p-3 bg-muted/20 border border-border/50 rounded-lg">
                      {(currentProduct.volumePricing?.rules ?? []).map((rule, idx) => (
                        <div key={idx} className="flex gap-2 items-end flex-wrap">
                          <div className="flex-1 min-w-[100px]">
                            <span className="text-[10px] text-muted-foreground">Every (qty)</span>
                            <input
                              type="number"
                              min={1}
                              value={rule.blockSize || ''}
                              onChange={e => {
                                const rules = [...(currentProduct.volumePricing?.rules ?? [])]
                                const val = parseInt(e.target.value, 10)
                                if (rules[idx]) rules[idx] = { ...rules[idx], blockSize: isNaN(val) || val < 1 ? 1 : val }
                                else rules[idx] = { blockSize: isNaN(val) || val < 1 ? 1 : val, pricePerUnit: rule.pricePerUnit ?? 0 }
                                setCurrentProduct({ ...currentProduct, volumePricing: { rules } })
                              }}
                              placeholder="e.g. 500"
                              className="w-full mt-0.5 px-3 py-2 bg-background border border-border rounded-lg text-sm"
                            />
                          </div>
                          <div className="flex-1 min-w-[100px]">
                            <span className="text-[10px] text-muted-foreground">Price/unit (Rs.)</span>
                            <input
                              type="number"
                              step="0.01"
                              min={0}
                              value={rule.pricePerUnit ?? ''}
                              onChange={e => {
                                const rules = [...(currentProduct.volumePricing?.rules ?? [])]
                                const val = parseFloat(e.target.value)
                                if (rules[idx]) rules[idx] = { ...rules[idx], pricePerUnit: isNaN(val) || val < 0 ? 0 : val }
                                else rules[idx] = { blockSize: rule.blockSize || 1, pricePerUnit: isNaN(val) || val < 0 ? 0 : val }
                                setCurrentProduct({ ...currentProduct, volumePricing: { rules } })
                              }}
                              placeholder="e.g. 2.50"
                              className="w-full mt-0.5 px-3 py-2 bg-background border border-border rounded-lg text-sm"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const rules = (currentProduct.volumePricing?.rules ?? []).filter((_, i) => i !== idx)
                              setCurrentProduct({ ...currentProduct, volumePricing: rules.length ? { rules } : undefined })
                            }}
                            className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                            title="Remove rule"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          const rules = [...(currentProduct.volumePricing?.rules ?? []), { blockSize: 1, pricePerUnit: 0 }]
                          setCurrentProduct({ ...currentProduct, volumePricing: { rules } })
                        }}
                        className="text-xs font-semibold text-primary hover:underline flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" /> Add volume rule
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-muted-foreground uppercase">Stock Level</label>
                    <input
                      type="number" required
                      value={currentProduct.stock}
                      onChange={e => setCurrentProduct({ ...currentProduct, stock: parseInt(e.target.value) })}
                      className="w-full mt-1 px-4 py-2 bg-background border border-border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-muted-foreground uppercase">Min Stock Alert</label>
                    <input
                      type="number" required
                      value={currentProduct.minStock}
                      onChange={e => setCurrentProduct({ ...currentProduct, minStock: parseInt(e.target.value) })}
                      className="w-full mt-1 px-4 py-2 bg-background border border-border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-muted-foreground uppercase">Reorder Level</label>
                    <input
                      type="number"
                      min={0}
                      value={currentProduct.reorderLevel ?? ''}
                      onChange={e => setCurrentProduct({ ...currentProduct, reorderLevel: parseInt(e.target.value) || undefined })}
                      placeholder="Alert when stock ≤"
                      className="w-full mt-1 px-4 py-2 bg-background border border-border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-muted-foreground uppercase">Reorder Qty</label>
                    <input
                      type="number"
                      min={0}
                      value={currentProduct.reorderQty ?? ''}
                      onChange={e => setCurrentProduct({ ...currentProduct, reorderQty: parseInt(e.target.value) || undefined })}
                      placeholder="Suggested order amount"
                      className="w-full mt-1 px-4 py-2 bg-background border border-border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-muted-foreground uppercase">Expiry Date</label>
                    <input
                      type="date"
                      value={currentProduct.expiryDate || ''}
                      onChange={e => setCurrentProduct({ ...currentProduct, expiryDate: e.target.value })}
                      className="w-full mt-1 px-4 py-2 bg-background border border-border rounded-lg"
                    />
                  </div>
                </div>
                <div className="flex gap-4 mt-8">
                  <button type="button" onClick={() => setShowEditModal(false)} className="flex-1 py-3 bg-muted rounded-lg font-bold hover:bg-muted/80 transition-colors">Cancel</button>
                  <button type="submit" disabled={isSubmitting} className="flex-1 py-3 bg-primary text-white rounded-lg font-bold glow-effect disabled:opacity-50">
                    {isSubmitting ? 'Saving...' : 'Update Book'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Batch Management Modal */}
        {showBatchModal && batchProduct && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <div className="glass-card max-w-2xl w-full border-primary/30 max-h-[85vh] flex flex-col">
              {/* Header */}
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-xl font-bold esales-text flex items-center gap-2">
                    <Layers className="w-5 h-5" />
                    Stock Batches
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {batchProduct.name} — Aggregate Stock: <span className="font-bold text-foreground">{batchProduct.stock.toLocaleString()}</span>
                    {(batchProduct as any).baseUnit && (
                      <span className="ml-1 text-primary font-semibold">{(batchProduct as any).baseUnit}</span>
                    )}
                  </p>
                </div>
                <button
                  onClick={() => { setShowBatchModal(false); setBatchProduct(null); setShowAddBatchForm(false) }}
                  className="p-2 rounded-lg hover:bg-muted transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Add Batch Button */}
              {!showAddBatchForm && (
                <button
                  onClick={() => {
                    setNewBatch({
                      sellingPrice: String(batchProduct.price || ''),
                      costPrice: String(batchProduct.cost || ''),
                      wholesalePrice: batchProduct.wholesalePrice ? String(batchProduct.wholesalePrice) : '',
                      discountPrice: batchProduct.discountPrice ? String(batchProduct.discountPrice) : '',
                      markedPrice: batchProduct.markedPrice ? String(batchProduct.markedPrice) : '',
                      currentStock: '',
                      receivedDate: new Date().toISOString().split('T')[0],
                      expiryDate: '',
                      notes: ''
                    })
                    setShowAddBatchForm(true)
                  }}
                  className="mb-4 w-full py-3 border-2 border-dashed border-primary/30 rounded-xl text-primary hover:bg-primary/5 transition-colors flex items-center justify-center gap-2 font-semibold text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Add New Batch
                </button>
              )}

              {/* Add Batch Form */}
              {showAddBatchForm && (
                <div className="mb-4 p-4 bg-muted/20 rounded-xl border border-border">
                  <h4 className="font-bold text-sm mb-3">New Stock Batch</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                    <div>
                      <label className="text-[10px] font-bold text-muted-foreground uppercase">Selling Price *</label>
                      <input type="number" step="0.01" value={newBatch.sellingPrice}
                        onChange={e => setNewBatch({ ...newBatch, sellingPrice: e.target.value })}
                        className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-lg text-sm" placeholder="0.00" />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-muted-foreground uppercase">Cost Price</label>
                      <input type="number" step="0.01" value={newBatch.costPrice}
                        onChange={e => setNewBatch({ ...newBatch, costPrice: e.target.value })}
                        className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-lg text-sm" placeholder="0.00" />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-muted-foreground uppercase">Stock Qty *</label>
                      <input type="number" value={newBatch.currentStock}
                        onChange={e => setNewBatch({ ...newBatch, currentStock: e.target.value })}
                        className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-lg text-sm" placeholder="0" />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-muted-foreground uppercase">Wholesale Price</label>
                      <input type="number" step="0.01" value={newBatch.wholesalePrice}
                        onChange={e => setNewBatch({ ...newBatch, wholesalePrice: e.target.value })}
                        className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-lg text-sm" placeholder="0.00" />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-muted-foreground uppercase">Discount Price</label>
                      <input type="number" step="0.01" value={newBatch.discountPrice}
                        onChange={e => setNewBatch({ ...newBatch, discountPrice: e.target.value })}
                        className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-lg text-sm" placeholder="0.00" />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-muted-foreground uppercase">Marked Price</label>
                      <input type="number" step="0.01" value={newBatch.markedPrice}
                        onChange={e => setNewBatch({ ...newBatch, markedPrice: e.target.value })}
                        className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-lg text-sm" placeholder="0.00" />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-muted-foreground uppercase">Received Date</label>
                      <input type="date" value={newBatch.receivedDate}
                        onChange={e => setNewBatch({ ...newBatch, receivedDate: e.target.value })}
                        className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-muted-foreground uppercase">Expiry Date</label>
                      <input type="date" value={newBatch.expiryDate}
                        onChange={e => setNewBatch({ ...newBatch, expiryDate: e.target.value })}
                        className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-muted-foreground uppercase">Notes</label>
                      <input type="text" value={newBatch.notes}
                        onChange={e => setNewBatch({ ...newBatch, notes: e.target.value })}
                        className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-lg text-sm" placeholder="Optional..." />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setShowAddBatchForm(false)}
                      className="flex-1 py-2 bg-muted rounded-lg text-sm font-semibold hover:bg-muted/80">Cancel</button>
                    <button onClick={handleAddBatch} disabled={isSubmitting}
                      className="flex-1 py-2 bg-primary text-white rounded-lg text-sm font-semibold disabled:opacity-50">
                      {isSubmitting ? 'Creating...' : 'Create Batch'}
                    </button>
                  </div>
                </div>
              )}

              {/* Batch List */}
              <div className="flex-1 overflow-y-auto pr-1 custom-scrollbar">
                {isBatchLoading ? (
                  <div className="py-12 text-center text-muted-foreground">
                    <LoadingSpinner size="sm" message="Loading batches..." />
                  </div>
                ) : productBatches.length === 0 ? (
                  <div className="py-12 text-center text-muted-foreground">
                    <Layers className="w-10 h-10 mx-auto mb-3 opacity-30" />
                    <p className="font-semibold">No stock batches</p>
                    <p className="text-xs mt-1">Batches are created automatically via GRN, or you can add one manually above.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {productBatches
                      .sort((a, b) => new Date(b.receivedDate || b.createdAt).getTime() - new Date(a.receivedDate || a.createdAt).getTime())
                      .map(batch => {
                        const isActive = batch.isActive && batch.currentStock > 0
                        return (
                          <div key={batch.id} className={`p-4 rounded-xl border ${isActive ? 'bg-muted/20 border-border' : 'bg-muted/5 border-border/50 opacity-60'}`}>
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-green-400' : 'bg-gray-500'}`} />
                                <span className="font-mono text-xs text-muted-foreground">{batch.batchNumber}</span>
                                {batch.grnId && (
                                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 font-bold uppercase">GRN</span>
                                )}
                                {!batch.grnId && (
                                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-400 font-bold uppercase">Manual</span>
                                )}
                              </div>
                              <button
                                onClick={() => handleDeleteBatch(batch.id)}
                                className="p-1.5 rounded-lg hover:bg-red-500/10 text-red-400 transition-colors"
                                title="Delete batch"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                              <div>
                                <p className="text-[10px] text-muted-foreground uppercase">Selling Price</p>
                                <p className="text-lg font-bold text-primary">Rs.{batch.sellingPrice.toFixed(2)}</p>
                              </div>
                              <div>
                                <p className="text-[10px] text-muted-foreground uppercase">Cost Price</p>
                                <p className="text-sm font-semibold">Rs.{batch.costPrice.toFixed(2)}</p>
                              </div>
                              <div>
                                <p className="text-[10px] text-muted-foreground uppercase">Stock</p>
                                <p className="text-sm font-semibold">
                                  {batch.currentStock} <span className="text-[10px] text-muted-foreground">/ {batch.receivedQuantity} received</span>
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-[10px] text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Received: {batch.receivedDate ? new Date(batch.receivedDate).toLocaleDateString() : '—'}
                              </span>
                              {(batch as any).receivingUnit && (batch as any).receivingQty != null && (
                                <span className="text-primary">
                                  {(batch as any).receivingQty} {(batch as any).receivingUnit} → {batch.receivedQuantity.toLocaleString()} {(batchProduct as any).baseUnit || 'units'}
                                </span>
                              )}
                              {batch.expiryDate && (
                                <span className="flex items-center gap-1 text-amber-400">
                                  <Calendar className="w-3 h-3" />
                                  Expires: {new Date(batch.expiryDate).toLocaleDateString()}
                                </span>
                              )}
                              {batch.supplierName && (
                                <span>Supplier: {batch.supplierName}</span>
                              )}
                            </div>
                            {(batch.wholesalePrice || batch.discountPrice || batch.markedPrice) && (
                              <div className="flex gap-3 mt-2 text-[10px] text-muted-foreground">
                                {batch.wholesalePrice && <span>Wholesale: Rs.{Number(batch.wholesalePrice).toFixed(2)}</span>}
                                {batch.discountPrice && <span className="text-yellow-400">Promo: Rs.{Number(batch.discountPrice).toFixed(2)}</span>}
                                {batch.markedPrice && <span>Marked: Rs.{Number(batch.markedPrice).toFixed(2)}</span>}
                              </div>
                            )}
                          </div>
                        )
                      })}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="mt-4 pt-4 border-t border-border">
                <button
                  onClick={() => { setShowBatchModal(false); setBatchProduct(null); setShowAddBatchForm(false) }}
                  className="w-full py-3 bg-muted hover:bg-muted/80 rounded-xl font-bold text-xs uppercase tracking-widest transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default InventoryPage

